import { useState, useRef, type SetStateAction } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { CheckCircle, Edit2, X, Check, AlertCircle } from 'lucide-react';
import { countries } from '@/data/countries';
import { resolveCountryName } from '@/utils/countryResolver';

interface AddressDetails {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

interface DocumentDetails {
  fullName: string;
  dateOfBirth: string;
  documentNumber: string;
  nationality: string;
  documentExpiry: string;
  noExpiryDate: boolean;
  documentIssueDate: string;
  issuingCountry: string;
  address: AddressDetails;
}

type EditableField = 'fullName' | 'dateOfBirth' | 'documentNumber' | 'nationality' | 'documentExpiry' | 'documentIssueDate' | 'issuingCountry' | 'address';

export function DocumentDetailsConfirmationStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  
  // Prefer whichever address source has the most complete data (has street)
  const poaAddr = data.poaExtractedAddress;
  const idAddr = data.extractedAddress;
  const extractedAddress = (poaAddr?.street ? poaAddr : null) || (idAddr?.street ? idAddr : null) || poaAddr || idAddr;
  
  const initialDetails: DocumentDetails = {
    fullName: `${data.extractedFirstName || ''} ${data.extractedLastName || ''}`.trim() || `${data.firstName} ${data.lastName}`.trim(),
    dateOfBirth: data.extractedDateOfBirth || data.dateOfBirth || '',
    documentNumber: data.extractedDocumentNumber || '',
    nationality: data.extractedNationality || '',
    documentExpiry: data.extractedDocumentExpiry || '',
    noExpiryDate: false,
    documentIssueDate: data.extractedDocumentIssueDate || '',
    issuingCountry: data.extractedDocumentCountry || '',
    address: {
      street: extractedAddress?.street || '',
      city: extractedAddress?.city || '',
      postalCode: extractedAddress?.postalCode || '',
      country: extractedAddress?.country || '',
    },
  };

  // Use a ref so the last inline "✓ save" click is respected even if React hasn't re-rendered yet.
  const detailsRef = useRef<DocumentDetails>(initialDetails);
  const [details, _setDetails] = useState<DocumentDetails>(initialDetails);

  const setDetails = (action: SetStateAction<DocumentDetails>) => {
    _setDetails((prev) => {
      const next =
        typeof action === 'function'
          ? (action as (p: DocumentDetails) => DocumentDetails)(prev)
          : action;
      detailsRef.current = next;
      return next;
    });
  };

  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editAddress, setEditAddress] = useState<AddressDetails>({ street: '', city: '', postalCode: '', country: '' });

  function formatAddress(addr: AddressDetails): string {
    const parts = [addr.street, addr.city, addr.postalCode, resolveCountryName(addr.country)].filter(Boolean);
    return parts.join(', ');
  }

  const handleEdit = (field: EditableField) => {
    setEditingField(field);
    if (field === 'address') {
      setEditAddress({ ...details.address });
    } else {
      setEditValue(String(details[field]));
    }
  };

  const handleSaveEdit = () => {
    if (editingField) {
      if (editingField === 'address') {
        setDetails(prev => ({
          ...prev,
          address: editAddress
        }));
      } else {
        setDetails(prev => ({
          ...prev,
          [editingField]: editValue
        }));
      }
      setEditingField(null);
      setEditValue('');
      setEditAddress({ street: '', city: '', postalCode: '', country: '' });
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setEditAddress({ street: '', city: '', postalCode: '', country: '' });
  };

  const handleNoExpiryToggle = () => {
    setDetails(prev => ({
      ...prev,
      noExpiryDate: !prev.noExpiryDate,
      documentExpiry: !prev.noExpiryDate ? '' : prev.documentExpiry
    }));
  };

  const isValid = () => {
    const addressToValidate = editingField === 'address' ? editAddress : details.address;
    const hasRequiredAddress = Boolean(
      addressToValidate.street?.trim() &&
      addressToValidate.city?.trim() &&
      addressToValidate.country?.trim()
    );

    return (
      details.fullName.trim() !== '' &&
      details.dateOfBirth.trim() !== '' &&
      details.documentNumber.trim() !== '' &&
      details.nationality.trim() !== '' &&
      (details.noExpiryDate || details.documentExpiry.trim() !== '') &&
      details.issuingCountry.trim() !== '' &&
      hasRequiredAddress
    );
  };

  const handleContinue = () => {
    // Use a ref so the last inline "✓ save" click is respected even if React hasn't re-rendered yet.
    const baseDetails = detailsRef.current;

    // If a field is currently being edited, treat its current input as the source of truth
    // (prevents losing manual edits when the user clicks Continue without pressing the inline ✓ button).
    const pendingDetails: DocumentDetails = (() => {
      if (!editingField) return baseDetails;
      if (editingField === 'address') return { ...baseDetails, address: editAddress };
      return { ...baseDetails, [editingField]: editValue } as DocumentDetails;
    })();

    // Parse full name back to first/last
    const nameParts = pendingDetails.fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Save confirmed data - store address in both extractedAddress and confirmedAddress
    // confirmedAddress is what user explicitly confirmed in this step
    updateData({
      firstName,
      lastName,
      dateOfBirth: pendingDetails.dateOfBirth,
      extractedDocumentNumber: pendingDetails.documentNumber,
      extractedNationality: pendingDetails.nationality,
      extractedDocumentExpiry: pendingDetails.noExpiryDate ? 'NO_EXPIRY' : pendingDetails.documentExpiry,
      extractedDocumentIssueDate: pendingDetails.documentIssueDate,
      extractedDocumentCountry: pendingDetails.issuingCountry,
      extractedAddress: pendingDetails.address,
      confirmedAddress: pendingDetails.address, // Also save as confirmed for audit trail
      // Also update the main address field for fallback compatibility
      address: pendingDetails.address,
      // Document details confirmed
      documentDetailsConfirmed: true,
    });

    goToNextStep();
  };

  const fields: Array<{ key: EditableField; label: string; type?: 'date' | 'text' | 'address' }> = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
    { key: 'documentNumber', label: 'Document No.' },
    { key: 'nationality', label: 'Citizenship' },
    { key: 'documentIssueDate', label: 'Issue Date', type: 'date' },
    { key: 'documentExpiry', label: 'Document Validity', type: 'date' },
    { key: 'issuingCountry', label: 'Issuing Country' },
    { key: 'address', label: 'Address', type: 'address' },
  ];

  const formatDisplayValue = (key: EditableField, value: string | AddressDetails): string => {
    if (key === 'address') {
      return formatAddress(value as AddressDetails);
    }
    const strValue = value as string;
    if (key === 'dateOfBirth' || key === 'documentExpiry' || key === 'documentIssueDate') {
      if (!strValue) return '—';
      if (key === 'documentExpiry' && details.noExpiryDate) return 'No Expiry';
      // Avoid UTC timezone shift by parsing date components directly
      const parts = strValue.split('-');
      if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
      }
      return strValue;
    }
    if (key === 'issuingCountry' || key === 'nationality') {
      return resolveCountryName(strValue) || '—';
    }
    return strValue || '—';
  };

  const isAddressEmpty = (addr: AddressDetails): boolean => {
    return !addr.street && !addr.city && !addr.postalCode && !addr.country;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2 tracking-widest text-center uppercase">
            Confirm Details
          </h1>
          <p className="text-muted-foreground text-center text-sm">
            Review the information extracted from your documents
          </p>
        </div>

        {/* Pre-filled box */}
        <div className="border border-border rounded-xl p-5 bg-card">
          {/* Header badge */}
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-accent" />
            <span className="text-accent text-sm font-medium">Pre-filled from verified documents</span>
          </div>

          <p className="text-muted-foreground mb-4 text-sm">I, the undersigned</p>

          {/* Fields */}
          <div className="space-y-3">
            {fields.map(({ key, label, type }) => {
              const isEditing = editingField === key;
              const value = details[key];
              const displayValue = formatDisplayValue(key, value);
              const isEmpty = key === 'address' 
                ? isAddressEmpty(value as AddressDetails) 
                : !value || value === '';
              
              // Skip expiry if noExpiryDate is checked and we're on that field
              if (key === 'documentExpiry' && details.noExpiryDate && !isEditing) {
                return (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-muted-foreground text-sm">{label}:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium text-sm">No Expiry</span>
                      <button
                        onClick={() => handleEdit(key)}
                        className="p-1 hover:bg-secondary rounded transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                );
              }

              // Issue date is optional
              if (key === 'documentIssueDate' && isEmpty && !isEditing) {
                return (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-muted-foreground text-sm">{label}:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground/50 text-sm italic">Not provided</span>
                      <button
                        onClick={() => handleEdit(key)}
                        className="p-1 hover:bg-secondary rounded transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                );
              }

              // Address field with multi-line form
              if (key === 'address') {
                if (isEditing) {
                  return (
                    <div key={key} className="py-3 border-b border-border/50 last:border-0">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-muted-foreground text-sm">{label}:</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSaveEdit}
                            className="p-1 hover:bg-accent/20 rounded transition-colors"
                          >
                            <Check className="w-4 h-4 text-accent" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 hover:bg-destructive/20 rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Street address"
                          value={editAddress.street}
                          onChange={(e) => setEditAddress(prev => ({ ...prev, street: e.target.value }))}
                          className="w-full px-3 py-2 text-sm rounded-lg bg-secondary border border-border focus:outline-none focus:border-accent text-foreground"
                          autoFocus
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="City"
                            value={editAddress.city}
                            onChange={(e) => setEditAddress(prev => ({ ...prev, city: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-secondary border border-border focus:outline-none focus:border-accent text-foreground"
                          />
                          <input
                            type="text"
                            placeholder="Postal code"
                            value={editAddress.postalCode}
                            onChange={(e) => setEditAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-secondary border border-border focus:outline-none focus:border-accent text-foreground"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Country"
                          value={editAddress.country}
                          onChange={(e) => setEditAddress(prev => ({ ...prev, country: e.target.value }))}
                          className="w-full px-3 py-2 text-sm rounded-lg bg-secondary border border-border focus:outline-none focus:border-accent text-foreground"
                        />
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={key} className="flex items-start justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-muted-foreground text-sm pt-0.5">{label}:</span>
                    <div className="flex items-start gap-2">
                      {isEmpty ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-orange-600 dark:text-orange-400 text-sm flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Required
                          </span>
                          <span className="text-xs text-muted-foreground">Tap edit to enter</span>
                        </div>
                      ) : (
                        <span className="text-foreground font-medium text-sm text-right max-w-[200px] whitespace-normal leading-tight">
                          {displayValue}
                        </span>
                      )}
                      <button
                        onClick={() => handleEdit(key)}
                        className="p-1 hover:bg-secondary rounded transition-colors flex-shrink-0"
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <span className="text-muted-foreground text-sm">{label}:</span>
                  
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type={type || 'text'}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="px-2 py-1 text-sm rounded bg-secondary border border-border focus:outline-none focus:border-accent text-foreground max-w-[150px]"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveEdit}
                        className="p-1 hover:bg-accent/20 rounded transition-colors"
                      >
                        <Check className="w-4 h-4 text-accent" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 hover:bg-destructive/20 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {isEmpty ? (
                        <span className="text-destructive text-sm flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Required
                        </span>
                      ) : (
                        <span className="text-foreground font-medium text-sm text-right max-w-[180px] truncate">
                          {displayValue}
                        </span>
                      )}
                      <button
                        onClick={() => handleEdit(key)}
                        className="p-1 hover:bg-secondary rounded transition-colors flex-shrink-0"
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* No expiry date checkbox */}
          <div className="mt-4 pt-3 border-t border-border/50">
            <label className="flex items-center gap-3 cursor-pointer">
              <div 
                onClick={handleNoExpiryToggle}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  details.noExpiryDate 
                    ? 'bg-accent border-accent' 
                    : 'border-border hover:border-accent/50'
                }`}
              >
                {details.noExpiryDate && <Check className="w-3 h-3 text-accent-foreground" />}
              </div>
              <span className="text-sm text-muted-foreground">
                My document does not have an expiry date
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!isValid()}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Confirm & Continue
          </button>
        </div>
      </div>
    </div>
  );
}
